/**
 * <jgd-rcc-recommendations> — lädt Produktempfehlungen über die Nimstrata
 * Retail Cloud Connect Storefront API (Google Vertex AI Search for Commerce)
 * und rendert sie als merchant-komponierte Horizon-Produktkarten.
 *
 * Ablauf:
 * 1. POST /v1/predict (Serving-Config + Kontext aus data-Attributen,
 *    visitorId aus dem `rcc-visitor-id`-Cookie des RCC-Pixels).
 * 2. Ergebnis-IDs → Section Rendering über die Predictive-Search-API:
 *    /search/suggest?q=id:… OR id:…&section_id=… re-rendert diese Section,
 *    wo das Liquid die _product-card-Blöcke aus
 *    predictive_search.resources.products baut. (Nur Predictive Search
 *    versteht id:-Queries; max. 10 Produkte pro Request → Chunking.)
 * 3. Slides werden in die Predict-Reihenfolge sortiert (Suche garantiert
 *    keine Reihenfolge) und ersetzen das Skeleton.
 * 4. Empfohlene Farbe: RCCs Katalog ist Produkt × Farbe
 *    (`<produkt-id>@<farbe>`, mit Bild dieser Farbe). Vor dem Einhängen wird
 *    je Karte die passende Variante aus `data-rcc-variants` (Section-Liquid)
 *    ermittelt — primär über den Bild-Dateinamen, Fallback Farb-Slug ↔
 *    Optionswert — und die Karte darauf umgestellt: Slide der Variante nach
 *    vorn (Karten-Slideshow ist im Carousel "nested" → disabled, `select()`
 *    ist wirkungslos, daher DOM-Reihenfolge), übrige Variantenbilder hidden
 *    (Stock-Regel: nur das erste Variantenbild sichtbar), die drei Karten-
 *    Links (`ref` productCardLink/cardGalleryLink/productTitleLink) auf
 *    `?variant=`. Nicht lieferbare oder nicht gefundene Variante → Karte
 *    bleibt wie von Liquid gerendert (jgd-card-variant).
 *
 * Keine Ergebnisse/Fehler → die Section blendet sich aus; im Theme-Editor
 * bleibt das Skeleton sichtbar, damit die Section konfigurierbar bleibt.
 *
 * Offen: Attribution — das attributionToken wird vorerst nur in
 * sessionStorage abgelegt; wie RCCs Pixel es bei Folge-Events erwartet,
 * ist noch zu verifizieren.
 */

import { predictProductIds } from './jgd-rcc-api.js';

/**
 * Normalisiert Farb-Slug (RCC: `evening_blue`, `sand_white`) und Optionswert
 * (Shopify: „evening blue", „türkis") auf eine vergleichbare Form:
 * Kleinbuchstaben, Diakritika entfernt, nur a–z0–9.
 * @param {string} value
 */
function normalizeColorKey(value) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Stellt eine (noch nicht eingehängte) Karte auf die von RCC empfohlene
 * Variante um: Slide der Variante nach vorn + andere Variantenbilder hidden,
 * Karten-Links auf `?variant=`. Kein Treffer / nicht lieferbar → unverändert.
 *
 * @param {Element} item - `.resource-list__item[data-rcc-product-id][data-rcc-variants]`
 * @param {{slug?: string, image?: string} | undefined} entry - Predict-Treffer des Produkts
 */
function applyRecommendedVariant(item, entry) {
  if (!entry || (!entry.image && !entry.slug)) return;

  /** @type {{v: number, m: number, f: string, a: boolean, o: string[]}[]} */
  let variants;
  try {
    variants = JSON.parse(item.dataset.rccVariants ?? '[]');
  } catch {
    return;
  }
  if (!Array.isArray(variants) || !variants.length) return;

  // primär Bild-Dateiname (eindeutig, keine Slug-Heuristik), Fallback Farb-Slug
  let match = entry.image ? variants.find((variant) => variant.f === entry.image) : undefined;
  if (!match && entry.slug) {
    const key = normalizeColorKey(entry.slug);
    match = variants.find((variant) => (variant.o ?? []).some((option) => normalizeColorKey(option) === key));
  }
  if (!match || !match.a) return;

  // Links: bestehende URL (inkl. Suggest-Tracking-Parameter) behalten, nur variant= setzen
  for (const link of item.querySelectorAll(
    'a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]'
  )) {
    if (!(link instanceof HTMLAnchorElement) || !link.getAttribute('href')) continue;
    const url = new URL(link.getAttribute('href') ?? '', window.location.origin);
    url.searchParams.set('variant', String(match.v));
    link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
  }

  // Bild: Slide der Variante nach vorn, andere Variantenbilder verstecken.
  const scroller = item.querySelector('slideshow-slides');
  const target = scroller?.querySelector(`slideshow-slide[slide-id="${match.m}"]`);
  if (!scroller || !target) return;

  const first = scroller.firstElementChild;
  if (first && first !== target) {
    // eager-Loading des bisher ersten Bilds auf das neue erste übertragen
    const firstImage = first.querySelector('img');
    const targetImage = target.querySelector('img');
    if (firstImage?.getAttribute('loading') === 'eager' && targetImage) {
      targetImage.setAttribute('loading', 'eager');
      firstImage.setAttribute('loading', 'lazy');
    }
    scroller.prepend(target);
  }

  Array.from(scroller.children).forEach((slide, index) => {
    if (!(slide instanceof HTMLElement)) return;
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    // --slideshow-timeline: --slide-N folgt der DOM-Position
    const style = slide.getAttribute('style');
    if (style) slide.setAttribute('style', style.replace(/--slide-\d+/, `--slide-${index}`));
    if (slide.hasAttribute('variant-image')) slide.hidden = index !== 0;
  });
}

class JgdRccRecommendations extends HTMLElement {
  /** @type {AbortController} */
  #controller;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#controller = new AbortController();

    // Lazy-Load (data-lazy-load): Predict-Call erst, wenn die Section sich
    // dem Viewport nähert — spart Vertex-Kosten für nie gescrollte Bereiche.
    // Im Theme-Editor immer sofort laden, damit die Section beim
    // Konfigurieren direkt Ergebnisse zeigt.
    if (this.hasAttribute('data-lazy-load') && !this.#designMode && 'IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.#disconnectObserver();
          this.#load();
        },
        { rootMargin: '200px' }
      );
      this.#observer.observe(this);
    } else {
      this.#load();
    }
  }

  disconnectedCallback() {
    this.#disconnectObserver();
    this.#controller.abort();
  }

  #disconnectObserver() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #designMode() {
    return this.hasAttribute('data-design-mode');
  }

  get #resultsContainer() {
    return this.querySelector('[ref="rccResults"]');
  }

  async #load() {
    const { project, servingConfig, pageSize, productId, sectionId, inStockOnly } = this.dataset;
    if (!project || !servingConfig || !sectionId) return this.#empty();

    try {
      const prediction = await predictProductIds({
        project,
        servingConfig,
        pageSize,
        productId,
        inStockOnly: inStockOnly === 'true',
        signal: this.#controller.signal,
      });
      if (!prediction?.ids.length) return this.#empty();

      const rendered = await this.#renderCards(prediction, sectionId);
      if (!rendered) return this.#empty();
    } catch (error) {
      if (!this.#controller.signal.aborted) this.#empty();
    }
  }

  /**
   * Holt die Section über die Predictive-Search-API (versteht id:-Queries,
   * max. 10 Produkte pro Request → Chunking), sortiert die Slides in
   * Predict-Reihenfolge, stellt jede Karte auf die empfohlene Variante um
   * und ersetzt das Skeleton.
   * @param {{ids: string[], entries?: Record<string, {slug: string, image: string}>}} prediction
   * @param {string} sectionId
   */
  async #renderCards({ ids, entries = {} }, sectionId) {
    const root = window.Shopify?.routes?.root ?? '/';
    const chunks = [];
    for (let i = 0; i < ids.length; i += 10) chunks.push(ids.slice(i, i + 10));

    const docs = await Promise.all(
      chunks.map(async (chunk) => {
        const params = new URLSearchParams({
          q: chunk.map((id) => `id:${id}`).join(' OR '),
          'resources[type]': 'product',
          'resources[limit]': String(chunk.length),
          section_id: sectionId,
        });
        const response = await fetch(`${root}search/suggest?${params}`, {
          signal: this.#controller.signal,
        });
        if (!response.ok) return null;
        return new DOMParser().parseFromString(await response.text(), 'text/html');
      })
    );

    const base = docs.filter(Boolean).map((doc) => doc.querySelector('[ref="rccResults"]'))
      .find((container) => container?.querySelector('[data-rcc-product-id]'));
    if (!base) return false;

    // Alle Items (über alle Chunks) einsammeln; sortierbares Element ist der
    // Slide-Wrapper (Carousel) bzw. das Item selbst.
    const order = new Map(ids.map((id, index) => [String(id), index]));
    const slides = docs
      .filter(Boolean)
      .flatMap((doc) => [...doc.querySelectorAll('[ref="rccResults"] [data-rcc-product-id]')])
      .map((item) => ({
        element: item.closest('slideshow-slide') ?? item,
        rank: order.get(item.dataset.rccProductId) ?? Number.MAX_SAFE_INTEGER,
      }))
      .sort((a, b) => a.rank - b.rank);
    if (!slides.length) return false;

    const slideParent = base.querySelector('[data-rcc-product-id]').closest('slideshow-slide')?.parentElement;
    slides.forEach(({ element }) => {
      const item = element.matches('[data-rcc-product-id]') ? element : element.querySelector('[data-rcc-product-id]');
      if (item) applyRecommendedVariant(item, entries[item.dataset.rccProductId]);
      (slideParent ?? base).appendChild(element);
    });

    this.#resultsContainer?.replaceWith(base);
    return true;
  }

  /** Keine Inhalte: Section ausblenden (im Editor bleibt das Skeleton stehen). */
  #empty() {
    if (this.#designMode) return;
    const section = this.closest('.shopify-section') ?? this;
    section.style.display = 'none';
  }
}

if (!customElements.get('jgd-rcc-recommendations')) {
  customElements.define('jgd-rcc-recommendations', JgdRccRecommendations);
}
